import React from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { formatCurrencyBDT } from "../../../utils/currency";
import { balanceText, MoneyImpact, round2 } from "./orderMath";

export interface ChangeRow {
    label: string;
    from?: string;
    to: string;
    tone?: "add" | "remove";
}

interface ConfirmChangeDialogProps {
    open: boolean;
    title: string;
    rows: ChangeRow[];
    /** Omit when the change can't affect the total (courier, channel). */
    impact?: MoneyImpact | null;
    warnings?: string[];
    /** Stock shortfalls — saving then needs an explicit "save anyway". */
    stockIssues?: string[];
    allowNegativeStock?: boolean;
    onAllowNegativeStockChange?: (allow: boolean) => void;
    error?: string;
    saving: boolean;
    confirmLabel?: string;
    onConfirm: () => void;
    onCancel: () => void;
}

const TONE_COLOR = { add: "success.main", remove: "error.main" } as const;

/** The one confirmation every drawer quick edit goes through. */
export const ConfirmChangeDialog: React.FC<ConfirmChangeDialogProps> = ({
    open,
    title,
    rows,
    impact,
    warnings = [],
    stockIssues = [],
    allowNegativeStock = false,
    onAllowNegativeStockChange,
    error,
    saving,
    confirmLabel = "Save change",
    onConfirm,
    onCancel,
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const blockedByStock = stockIssues.length > 0 && !allowNegativeStock;
    const totalChanges = impact && round2(impact.newTotal) !== round2(impact.oldTotal);

    return (
        <Dialog open={open} onClose={saving ? undefined : onCancel} fullScreen={fullScreen} fullWidth maxWidth="xs">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={1.25}>
                    {rows.map((row, i) => (
                        <Box key={i} sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
                            <Typography
                                variant="body2"
                                sx={{ flex: "1 1 160px", minWidth: 0, color: row.tone ? TONE_COLOR[row.tone] : undefined }}
                            >
                                {row.label}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                {row.from !== undefined && (
                                    <>
                                        <Typography variant="body2" color="text.secondary">
                                            {row.from}
                                        </Typography>
                                        <ArrowForward sx={{ fontSize: 14, color: "text.secondary" }} aria-label="to" />
                                    </>
                                )}
                                <Typography variant="body2" fontWeight="bold">
                                    {row.to}
                                </Typography>
                            </Box>
                        </Box>
                    ))}

                    {impact && (
                        <>
                            <Divider />
                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                                <Typography variant="body2">Order total</Typography>
                                <Typography variant="body2" fontWeight="bold" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                    {totalChanges ? `${formatCurrencyBDT(impact.oldTotal)} → ` : ""}
                                    {formatCurrencyBDT(impact.newTotal)}
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Paid {formatCurrencyBDT(impact.paid)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {balanceText(impact.newTotal, impact.paid)}
                                </Typography>
                            </Box>
                        </>
                    )}

                    {warnings.map((w, i) => (
                        <Alert key={i} severity="warning" sx={{ py: 0.25 }}>
                            {w}
                        </Alert>
                    ))}

                    {stockIssues.length > 0 && (
                        <Alert severity="error" sx={{ py: 0.25 }}>
                            <Typography variant="body2" fontWeight="bold">
                                Not enough stock
                            </Typography>
                            {stockIssues.map((s, i) => (
                                <Typography key={i} variant="body2">
                                    {s}
                                </Typography>
                            ))}
                            <FormControlLabel
                                sx={{ mt: 0.5 }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={allowNegativeStock}
                                        onChange={(e) => onAllowNegativeStockChange?.(e.target.checked)}
                                    />
                                }
                                label="Save anyway — stock will go negative"
                            />
                        </Alert>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={saving} color="inherit">
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={onConfirm}
                    disabled={saving || blockedByStock}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {saving ? "Saving…" : confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
